import { localizedPath, type LocaleId } from '@bannermatic/core';
import { translate } from '../lib/i18n';
import { LandingPage } from '../pages/Landing';
import { LEGAL_DOCS, type LegalKind } from '../pages/legal/content';
import { LEGAL } from '../pages/legal/meta';
import { ContactPage } from './ContactPage';
import { LegalPage } from '../pages/legal/LegalPage';
import { PUBLIC_PATHS } from './catalog';
import type { PageHead } from './head';
import type { PublicPageDef } from './PublicRoute';
import { loadedMarketingCopy, type HubId, type MarketingPageId } from './content';
import { HUB_PATHS, hubOf, PAGE_PATHS } from './content/shared';
import { HubPage, MarketingPage } from './MarketingPage';
import {
  aspectRatio,
  categoryPath,
  PUBLIC_CATEGORIES,
  PUBLIC_SIZES,
  PUBLIC_TEMPLATES,
  SIZES_PATH,
  sizePath,
  TEMPLATES_PATH,
  templatePath,
  templatesIn,
  templateName,
} from './programmatic';
import { SizePage, SizesHubPage } from './SizePages';
import { fill, TemplateCategoryPage, TemplatePage, TemplatesHubPage } from './TemplatePages';
import type { GalleryCategory, GalleryEntry, SizePreset } from '@bannermatic/core';
import {
  breadcrumbs,
  contactPage,
  itemList,
  faqPage,
  organization,
  softwareApplication,
  webPage,
  website,
} from './schema';

/** Shortens text for a meta description at a word boundary. */
export function clip(text: string, max = 158): string {
  if (text.length <= max) return text;
  const cut = text.slice(0, max - 1);
  return `${cut.slice(0, cut.lastIndexOf(' ')).replace(/[,.;:—-]+$/, '')}…`;
}

const FAQ_COUNT = 8;

function landingHead(locale: LocaleId, site: string): PageHead {
  const t = (key: Parameters<typeof translate>[1]) => translate(locale, key);
  const description = t('landing.metaDescription');
  return {
    title: t('landing.metaTitle'),
    description,
    jsonLd: [
      organization(site),
      website(site, locale),
      softwareApplication(site, description),
      faqPage(
        Array.from({ length: FAQ_COUNT }, (_, i) => ({
          q: t(`landing.q${i + 1}` as 'landing.q1'),
          a: t(`landing.a${i + 1}` as 'landing.a1'),
        })),
      ),
    ],
  };
}

function legalHead(kind: LegalKind) {
  return (locale: LocaleId, site: string): PageHead => {
    const doc = LEGAL_DOCS[kind][locale];
    const title = `${doc.title} — Bannermatic`;
    const description = clip(doc.intro);
    const url = site + localizedPath(locale, `/${kind}`);
    return { title, description, jsonLd: [webPage(site, url, title, description, locale)] };
  };
}

function hubHead(hub: HubId) {
  return (locale: LocaleId, site: string): PageHead => {
    const copy = loadedMarketingCopy(locale);
    const h = copy.hubs[hub];
    const url = site + localizedPath(locale, HUB_PATHS[hub]);
    return {
      title: h.meta.title,
      description: h.meta.description,
      jsonLd: [
        webPage(site, url, h.meta.title, h.meta.description, locale),
        breadcrumbs([
          { name: copy.ui.home, url: site + localizedPath(locale, '/') },
          { name: h.label, url },
        ]),
      ],
    };
  };
}

function marketingHead(id: MarketingPageId) {
  return (locale: LocaleId, site: string): PageHead => {
    const copy = loadedMarketingCopy(locale);
    const page = copy.pages[id];
    const hub = hubOf(id);
    const url = site + localizedPath(locale, PAGE_PATHS[id]);
    return {
      title: page.meta.title,
      description: page.meta.description,
      jsonLd: [
        webPage(site, url, page.meta.title, page.meta.description, locale),
        breadcrumbs([
          { name: copy.ui.home, url: site + localizedPath(locale, '/') },
          { name: copy.hubs[hub].label, url: site + localizedPath(locale, HUB_PATHS[hub]) },
          { name: page.label, url },
        ]),
        faqPage(page.faq.map(({ q, a }) => ({ q, a }))),
      ],
    };
  };
}

function contactHead(locale: LocaleId, site: string): PageHead {
  const copy = loadedMarketingCopy(locale);
  const c = copy.contact;
  const url = site + localizedPath(locale, '/contact');
  return {
    title: c.meta.title,
    description: c.meta.description,
    jsonLd: [
      organization(site),
      contactPage(site, url, c.meta.title, LEGAL.email),
      breadcrumbs([
        { name: copy.ui.home, url: site + localizedPath(locale, '/') },
        { name: c.label, url },
      ]),
    ],
  };
}

const crumb = (site: string, locale: LocaleId, name: string, path: string) => ({
  name,
  url: site + localizedPath(locale, path),
});

function templatesHubHead(locale: LocaleId, site: string): PageHead {
  const copy = loadedMarketingCopy(locale);
  const g = copy.gallery;
  const url = site + localizedPath(locale, TEMPLATES_PATH);
  return {
    title: g.hub.meta.title,
    description: g.hub.meta.description,
    jsonLd: [
      webPage(site, url, g.hub.meta.title, g.hub.meta.description, locale),
      breadcrumbs([crumb(site, locale, copy.ui.home, '/'), { name: g.hub.label, url }]),
      itemList(
        PUBLIC_CATEGORIES.map((c) => crumb(site, locale, g.categories[c].name, categoryPath(c))),
      ),
    ],
  };
}

function categoryHead(category: GalleryCategory) {
  return (locale: LocaleId, site: string): PageHead => {
    const copy = loadedMarketingCopy(locale);
    const g = copy.gallery;
    const cat = g.categories[category];
    const url = site + localizedPath(locale, categoryPath(category));
    return {
      title: cat.metaTitle,
      description: cat.metaDescription,
      jsonLd: [
        webPage(site, url, cat.metaTitle, cat.metaDescription, locale),
        breadcrumbs([
          crumb(site, locale, copy.ui.home, '/'),
          crumb(site, locale, g.hub.label, TEMPLATES_PATH),
          { name: cat.name, url },
        ]),
        itemList(
          templatesIn(category).map((e) =>
            crumb(site, locale, templateName(e, locale), templatePath(e)),
          ),
        ),
      ],
    };
  };
}

function templateHead(entry: GalleryEntry) {
  return (locale: LocaleId, site: string): PageHead => {
    const copy = loadedMarketingCopy(locale);
    const g = copy.gallery;
    const vars = {
      name: templateName(entry, locale),
      category: g.categories[entry.category].name,
      size: `${entry.width} × ${entry.height}`,
    };
    const title = fill(g.template.metaTitle, vars);
    const description = fill(g.template.metaDescription, vars);
    const url = site + localizedPath(locale, templatePath(entry));
    return {
      title,
      description,
      jsonLd: [
        webPage(site, url, title, description, locale),
        breadcrumbs([
          crumb(site, locale, copy.ui.home, '/'),
          crumb(site, locale, g.hub.label, TEMPLATES_PATH),
          crumb(site, locale, vars.category, categoryPath(entry.category)),
          { name: vars.name, url },
        ]),
      ],
    };
  };
}

function sizesHubHead(locale: LocaleId, site: string): PageHead {
  const copy = loadedMarketingCopy(locale);
  const s = copy.sizes;
  const url = site + localizedPath(locale, SIZES_PATH);
  return {
    title: s.hub.meta.title,
    description: s.hub.meta.description,
    jsonLd: [
      webPage(site, url, s.hub.meta.title, s.hub.meta.description, locale),
      breadcrumbs([crumb(site, locale, copy.ui.home, '/'), { name: s.hub.label, url }]),
      itemList(PUBLIC_SIZES.map((p) => crumb(site, locale, s.presets[p.id].name, sizePath(p)))),
    ],
  };
}

function sizeHead(preset: SizePreset) {
  return (locale: LocaleId, site: string): PageHead => {
    const copy = loadedMarketingCopy(locale);
    const s = copy.sizes;
    const vars = {
      name: s.presets[preset.id].name,
      w: preset.width,
      h: preset.height,
      ratio: aspectRatio(preset.width, preset.height),
    };
    const title = fill(s.page.metaTitle, vars);
    const description = fill(s.page.metaDescription, vars);
    const url = site + localizedPath(locale, sizePath(preset));
    return {
      title,
      description,
      jsonLd: [
        webPage(site, url, title, description, locale),
        breadcrumbs([
          crumb(site, locale, copy.ui.home, '/'),
          crumb(site, locale, s.hub.label, SIZES_PATH),
          { name: vars.name, url },
        ]),
        faqPage(s.page.faq.map(({ q, a }) => ({ q: fill(q, vars), a: fill(a, vars) }))),
      ],
    };
  };
}

const PrivacyPage = () => <LegalPage kind="privacy" />;
const TermsPage = () => <LegalPage kind="terms" />;

const page = (path: string, def: Omit<PublicPageDef, 'path' | 'locales'>): PublicPageDef => ({
  path,
  locales: PUBLIC_PATHS[path],
  ...def,
});

/** Every pre-rendered public page; the router and the pre-render script both read this list. */
export const PUBLIC_PAGES: PublicPageDef[] = [
  page('/', { component: LandingPage, head: landingHead }),
  page('/privacy', { component: PrivacyPage, head: legalHead('privacy') }),
  page('/terms', { component: TermsPage, head: legalHead('terms') }),
  page('/contact', { component: ContactPage, head: contactHead }),
  ...(Object.keys(HUB_PATHS) as HubId[]).map((hub) =>
    page(HUB_PATHS[hub], { component: () => <HubPage hub={hub} />, head: hubHead(hub) }),
  ),
  page(TEMPLATES_PATH, { component: TemplatesHubPage, head: templatesHubHead }),
  ...PUBLIC_CATEGORIES.map((c) =>
    page(categoryPath(c), {
      component: () => <TemplateCategoryPage category={c} />,
      head: categoryHead(c),
    }),
  ),
  ...PUBLIC_TEMPLATES.map((e) =>
    page(templatePath(e), { component: () => <TemplatePage entry={e} />, head: templateHead(e) }),
  ),
  page(SIZES_PATH, { component: SizesHubPage, head: sizesHubHead }),
  ...PUBLIC_SIZES.map((p) =>
    page(sizePath(p), { component: () => <SizePage preset={p} />, head: sizeHead(p) }),
  ),
  ...(Object.keys(PAGE_PATHS) as MarketingPageId[]).map((id) =>
    page(PAGE_PATHS[id], { component: () => <MarketingPage id={id} />, head: marketingHead(id) }),
  ),
];
